import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/GalleryPage.css';
import ImageLoader from '../components/PhotoGallery/ImageLoader';
import { ImageData } from '../types/gallery';

const GalleryPage: React.FC = () => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [touchStart, setTouchStart] = useState<number>(0);
  const [touchEnd, setTouchEnd] = useState<number>(0);
  const [isChanging, setIsChanging] = useState(false);

  const handleImagesLoaded = useCallback((loadedImages: ImageData[]) => {
    setImages(loadedImages);
    setLoading(false);
  }, []);

  const openLightbox = (imageUrl: string) => {
    setSelectedImage(imageUrl);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!selectedImage || isChanging) return;

    setIsChanging(true);

    const currentIndex = images.findIndex(img => img.url === selectedImage);
    let newIndex: number;

    if (direction === 'prev') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : images.length - 1;
    } else {
      newIndex = currentIndex < images.length - 1 ? currentIndex + 1 : 0;
    }

    const nextImageUrl = images[newIndex].url;

    // Preload the next image
    const img = new Image();
    img.onload = () => {
      // Image is loaded, now change it
      setTimeout(() => {
        setSelectedImage(nextImageUrl);
        setTimeout(() => setIsChanging(false), 300);
      }, 150);
    };
    img.onerror = () => {
      // If image fails to load, still proceed
      setTimeout(() => {
        setSelectedImage(nextImageUrl);
        setTimeout(() => setIsChanging(false), 300);
      }, 150);
    };
    img.src = nextImageUrl;
  };

  // Touch event handlers for swipe detection
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(0); // Reset touchEnd
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      navigateImage('next');
    } else if (isRightSwipe) {
      navigateImage('prev');
    }
  };

  // Keyboard navigation for desktop
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!selectedImage) return;

    switch (e.key) {
      case 'ArrowLeft':
        navigateImage('prev');
        break;
      case 'ArrowRight':
        navigateImage('next');
        break;
      case 'Escape':
        closeLightbox();
        break;
    }
  }, [selectedImage]);

  // Add keyboard event listener when lightbox is open
  React.useEffect(() => {
    if (selectedImage) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [selectedImage, handleKeyDown]);

  console.log('[DEBUG GalleryPage] Rendering with SEO Helmet tags');

  return (
    <div className="gallery-page">
      <Helmet>
        <title>Gallery | SpitFire Braai Catering - Event Photos</title>
        <meta name="description" content="Browse photos from SpitFire Braai Catering events — launch celebrations, corporate functions, and private braai gatherings across South Africa." />
        <meta property="og:title" content="Gallery | SpitFire Braai Catering - Event Photos" />
        <meta property="og:description" content="Browse photos from SpitFire Braai Catering events and celebrations." />
        <meta property="og:url" content="https://www.spitfirebraai.co.za/gallery" />
        <link rel="canonical" href="https://www.spitfirebraai.co.za/gallery" />
      </Helmet>
      <ImageLoader onImagesLoaded={handleImagesLoaded} />

      <div className="container">
        <header className="gallery-header">
          <h1>Launch</h1>
          <p>Celebrating our launch with delicious braai, great company, and unforgettable moments.</p>
        </header>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading gallery images...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="no-images-container">
            <p>No images available at the moment. Please check back later!</p>
          </div>
        ) : (
          <div className="photo-grid">
            {images.map((image, index) => (
              <div key={image.key} className="photo-item" onClick={() => openLightbox(image.url)}>
                <img
                  src={image.thumbnailUrl}
                  alt={image.caption ? image.caption : `SpitFire braai catering launch event — photo ${index + 1}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        )}

        {selectedImage && (
          <div className="lightbox" onClick={closeLightbox}>
            <div
              className={`lightbox-content ${isChanging ? 'changing' : ''}`}
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <button className="lightbox-close" onClick={closeLightbox}>×</button>
              <button className="lightbox-nav lightbox-prev" onClick={() => navigateImage('prev')}>‹</button>
              <img
                src={selectedImage}
                alt="Full size view of SpitFire braai catering event"
                draggable={false}
              />
              <button className="lightbox-nav lightbox-next" onClick={() => navigateImage('next')}>›</button>
              <div className="swipe-hint">
                <span>Swipe to navigate</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GalleryPage;